// System Settings — super_admin only.
//
// Each major policy lives in its own card on this page; new policies
// drop in by adding another card.  Saving writes through to /settings
// (PUT) and asks the SettingsContext to refresh so every other page —
// Reservations, Check In / Out, future billing flows — picks up the
// new rule immediately without a hard reload.

import React, { useEffect, useState } from "react";
import axios from "axios";
import { settingsUrl, smsBalanceUrl } from "../../BaseURL";
import { useSettings } from "../../context/SettingsContext";

export default function Settings() {
  const { settings, meta, loading, refresh } = useSettings();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  // SMS API-key visibility toggle.  Default hidden so a casual screenshot
  // doesn't leak credentials.
  const [showKey, setShowKey] = useState(false);

  // Balance lookup — lets the operator verify the key works without
  // leaving the Settings page.  We don't auto-fetch so a half-typed
  // key doesn't spam the gateway on every keystroke.
  const [balance, setBalance] = useState(null);   // { ok, balance, currency, error }
  const [balanceLoading, setBalanceLoading] = useState(false);
  const checkBalance = async () => {
    setBalanceLoading(true);
    setBalance(null);
    try {
      const r = await axios.get(smsBalanceUrl);
      setBalance(r.data || { ok: false, error: "No response" });
    } catch (e) {
      setBalance({ ok: false, error: e.response?.data?.error || e.response?.data?.message || "Could not reach gateway" });
    } finally {
      setBalanceLoading(false);
    }
  };

  // Seed the editable draft from the current settings.  When the cache
  // refreshes (e.g. after another tab saved) we re-seed so the form
  // stays in sync without trampling unsaved edits the operator is
  // mid-typing.
  useEffect(() => {
    if (!loading) {
      setDraft((prev) => ({ ...settings, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4500);
  };

  const setKey = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const isDirty = Object.keys(draft).some(
    (k) => String(draft[k]) !== String(settings[k])
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of Object.keys(draft)) {
        if (String(draft[k]) !== String(settings[k])) payload[k] = draft[k];
      }
      if (Object.keys(payload).length === 0) { showMsg("success", "No changes."); return; }
      const res = await axios.put(settingsUrl, { settings: payload });
      if (!res.data?.success) {
        showMsg("error", res.data?.message || "Save failed");
        return;
      }
      await refresh();
      showMsg("success", "Settings saved.");
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, color: "#2c1f14", fontWeight: 500, marginBottom: 4 }}>
          System Settings
        </div>
        <div style={{ fontSize: 14.5, color: "#6b5142" }}>
          Facility-wide policies. Only super admins can change these — every other staff screen reads them live.
        </div>
      </div>

      {msg.text && (
        <div style={{
          padding: "12px 16px", borderRadius: 8, marginBottom: 16,
          background: msg.type === "success" ? "#eaf5ee" : "#fef0ee",
          color:      msg.type === "success" ? "#2d6a4f" : "#a93226",
          border:     `1px solid ${msg.type === "success" ? "#a8d5b5" : "#f5c5bc"}`,
          fontSize: 14, fontWeight: 400,
        }}>
          {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
        </div>
      )}

      {/* ── Deposit Policy ───────────────────────────────────────────── */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-hd">
          <span className="panel-hd-title">💰 Deposit Policy</span>
          <span className={`badge ${draft["deposit.required"] ? "badge-green" : "badge-gray"}`}>
            {draft["deposit.required"] ? "Active" : "Off"}
          </span>
        </div>
        <div style={{ padding: 22 }}>
          <p style={{ fontSize: 14, color: "#3a2a1a", lineHeight: 1.6, marginBottom: 18 }}>
            When this is on, every new reservation and walk-in check-in
            must record at least the minimum deposit below before the
            system will save it. The front desk will see a hint on the
            booking form and the submit button stays disabled until the
            amount is collected.
          </p>

          {/* Toggle */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px", borderRadius: 10,
            border: "1px solid rgba(180,140,80,.22)",
            background: "#fdf8f2",
            marginBottom: 16,
          }}>
            <div>
              <div style={{ fontSize: 14, color: "#2c1f14", fontWeight: 500 }}>
                Require a deposit before saving
              </div>
              <div style={{ fontSize: 12.5, color: "#6b5142", marginTop: 2 }}>
                Applies to both new reservations and walk-in check-ins.
              </div>
            </div>
            <Toggle
              checked={!!draft["deposit.required"]}
              onChange={(v) => setKey("deposit.required", v)}
            />
          </div>

          {/* Minimum amount */}
          <div className="f-group" style={{ marginBottom: 0, opacity: draft["deposit.required"] ? 1 : .55 }}>
            <label className="f-label">Minimum deposit amount (GH₵)</label>
            <input
              className="f-input"
              type="number" min="0" step="0.01"
              placeholder="e.g. 200"
              value={draft["deposit.min_amount"] ?? ""}
              onChange={(e) => setKey("deposit.min_amount", e.target.value)}
              disabled={!draft["deposit.required"]}
            />
            <div style={{ fontSize: 12, color: "#8a7060", marginTop: 6 }}>
              The system uses the smaller of this amount or the booking's
              own total — a 10-cedi micro-stay won't be forced to pay a
              200-cedi deposit.
            </div>
          </div>

          {meta["deposit.required"]?.updated_at && (
            <div style={{ marginTop: 14, fontSize: 11, color: "#8a7060", letterSpacing: ".06em" }}>
              Last updated {new Date(meta["deposit.required"].updated_at).toLocaleString("en-GB", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}
            </div>
          )}
        </div>
      </div>

      {/* ── SMS Gateway ──────────────────────────────────────────────── */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-hd">
          <span className="panel-hd-title">💬 SMS Gateway (smsonlinegh.com)</span>
          <span className={`badge ${draft["sms.enabled"] ? "badge-green" : "badge-gray"}`}>
            {draft["sms.enabled"] ? "Active" : "Off"}
          </span>
        </div>
        <div style={{ padding: 22 }}>
          <p style={{ fontSize: 14, color: "#3a2a1a", lineHeight: 1.6, marginBottom: 12 }}>
            Powers every system-generated SMS: the welcome message on
            check-in, reservation confirmations, payment receipts,
            cancellations, and any ad-hoc messages you send from the
            <strong> Messages </strong> tab.
          </p>

          {/* What to generate from the provider portal */}
          <div style={{
            padding: "12px 14px", borderRadius: 8, marginBottom: 16,
            background: "rgba(200,160,90,.07)", border: "1px dashed rgba(200,160,90,.35)",
            fontSize: 13, color: "#4a3828", lineHeight: 1.6,
          }}>
            <div style={{ fontWeight: 500, marginBottom: 4, color: "#2c1f14" }}>
              What to grab from your SMS Online GH portal:
            </div>
            <ol style={{ paddingLeft: 18, margin: 0 }}>
              <li>
                Sign in at <strong>dev.smsonlinegh.com</strong> (or
                <strong> portal.smsonlinegh.com</strong>) with your account.
              </li>
              <li>
                Go to <strong>Settings → API Keys</strong>, click
                <strong> Generate Key</strong>, and copy the value into
                the <em>API Key</em> field below.
              </li>
              <li>
                In the same portal, go to <strong>Settings → Sender IDs</strong>
                and register an alpha sender (e.g. your hotel name, max 11
                characters).  Paste that exact string into the
                <em> Sender ID</em> field.
              </li>
            </ol>
          </div>

          {/* Master enable toggle */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px", borderRadius: 10,
            border: "1px solid rgba(180,140,80,.22)",
            background: "#fdf8f2",
            marginBottom: 16,
          }}>
            <div>
              <div style={{ fontSize: 14, color: "#2c1f14", fontWeight: 500 }}>
                Auto-send system SMS
              </div>
              <div style={{ fontSize: 12.5, color: "#6b5142", marginTop: 2 }}>
                Switch off to silence every automatic message (manual sends from the Messages page still work).
              </div>
            </div>
            <Toggle
              checked={!!draft["sms.enabled"]}
              onChange={(v) => setKey("sms.enabled", v)}
            />
          </div>

          {/* API Key */}
          <div className="f-group">
            <label className="f-label">API Key *</label>
            <div style={{ position: "relative" }}>
              <input
                className="f-input"
                type={showKey ? "text" : "password"}
                placeholder="Paste the API key from your SMS Online GH portal"
                value={draft["sms.api_key"] ?? ""}
                onChange={(e) => setKey("sms.api_key", e.target.value)}
                style={{ paddingRight: 80 }}
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowKey((s) => !s)}
                style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 11, color: "#8a7060", letterSpacing: ".12em",
                  textTransform: "uppercase",
                }}
              >
                {showKey ? "Hide" : "Show"}
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#8a7060", marginTop: 6 }}>
              Sent as <code style={{ fontFamily: "monospace" }}>Authorization: key &lt;your-key&gt;</code> on every request.
            </div>
          </div>

          {/* Sender ID */}
          <div className="f-group">
            <label className="f-label">Sender ID</label>
            <input
              className="f-input"
              maxLength={11}
              placeholder="e.g. BISONG"
              value={draft["sms.sender_id"] ?? ""}
              onChange={(e) => setKey("sms.sender_id", e.target.value)}
            />
            <div style={{ fontSize: 12, color: "#8a7060", marginTop: 6 }}>
              Alphanumeric, up to 11 characters. Must be pre-registered with your provider.
            </div>
          </div>

          {/* Advanced — API base + paths */}
          <details style={{ marginTop: 4 }}>
            <summary style={{ cursor: "pointer", fontSize: 12, color: "#8a7060", letterSpacing: ".06em" }}>
              Advanced (gateway endpoints)
            </summary>
            <div style={{ paddingTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div className="f-group" style={{ marginBottom: 0 }}>
                <label className="f-label">API Base URL</label>
                <input
                  className="f-input"
                  placeholder="https://api.smsonlinegh.com/v5"
                  value={draft["sms.api_base"] ?? ""}
                  onChange={(e) => setKey("sms.api_base", e.target.value)}
                />
              </div>
              <div className="f-group" style={{ marginBottom: 0 }}>
                <label className="f-label">Send Path</label>
                <input
                  className="f-input"
                  placeholder="/messages/send"
                  value={draft["sms.send_path"] ?? ""}
                  onChange={(e) => setKey("sms.send_path", e.target.value)}
                />
              </div>
              <div className="f-group" style={{ marginBottom: 0 }}>
                <label className="f-label">Balance Path</label>
                <input
                  className="f-input"
                  placeholder="/credits"
                  value={draft["sms.balance_path"] ?? ""}
                  onChange={(e) => setKey("sms.balance_path", e.target.value)}
                />
              </div>
            </div>
          </details>

          {/* Test / balance check */}
          <div style={{
            marginTop: 18, padding: "14px 16px", borderRadius: 10,
            border: "1px solid rgba(180,140,80,.22)", background: "#fff",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 13, color: "#2c1f14", fontWeight: 500 }}>
                Verify the gateway responds
              </div>
              <div style={{ fontSize: 12.5, color: "#6b5142", marginTop: 2 }}>
                {balance == null ? (
                  "Save your settings first, then click to ping the provider for your current credit balance."
                ) : balance.ok ? (
                  <span style={{ color: "#2d6a4f" }}>
                    ✅ Balance: <strong>{balance.balance ?? "—"}</strong> {balance.currency || ""}
                  </span>
                ) : (
                  <span style={{ color: "#a93226" }}>
                    ⚠️ {balance.error || "Could not reach gateway."}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="btn-outline"
              onClick={checkBalance}
              disabled={balanceLoading}
              style={{ whiteSpace: "nowrap" }}>
              {balanceLoading ? "Checking…" : "Check balance"}
            </button>
          </div>

          {meta["sms.api_key"]?.updated_at && (
            <div style={{ marginTop: 14, fontSize: 11, color: "#8a7060", letterSpacing: ".06em" }}>
              Last updated {new Date(meta["sms.api_key"].updated_at).toLocaleString("en-GB", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}
            </div>
          )}
        </div>
      </div>

      {/* Sticky save bar */}
      <div style={{
        display: "flex", justifyContent: "flex-end", gap: 10,
        padding: "14px 0", position: "sticky", bottom: 0,
        background: "linear-gradient(180deg, transparent 0%, rgba(245,237,226,.96) 30%)",
      }}>
        <button
          className="btn-outline"
          disabled={!isDirty || saving}
          onClick={() => setDraft({ ...settings })}>
          Discard
        </button>
        <button
          className="btn-dark"
          disabled={!isDirty || saving}
          onClick={save}
          style={{ minWidth: 160 }}>
          {saving ? "Saving…" : isDirty ? "Save changes" : "Saved"}
        </button>
      </div>
    </div>
  );
}

// Small pill-style toggle used by the policy cards.
function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 52, height: 28, borderRadius: 999,
        background: checked ? "linear-gradient(135deg, #c8a05a, #a8814a)" : "#d8c3ad",
        border: "none", cursor: "pointer",
        position: "relative", flexShrink: 0,
        transition: "background .2s",
      }}>
      <span style={{
        position: "absolute",
        top: 3, left: checked ? 27 : 3,
        width: 22, height: 22, borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 4px rgba(40,20,5,.18)",
        transition: "left .2s",
      }}/>
    </button>
  );
}
